import { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import type { PmoUserRecord } from '../../store/useUserStore';
import type { PmoRole } from '../../store/useAuthStore';
import { useProjectStore } from '../../store/useProjectStore';

const ROLE_OPTIONS: { value: PmoRole; label: string; description: string }[] = [
  { value: 'super_admin', label: 'Super Admin', description: 'Full access: user administration, all projects, settings' },
  { value: 'project_admin', label: 'Project Admin', description: 'Create & manage projects, work items, funding' },
  { value: 'project_resource', label: 'Project Resource', description: 'Update tasks in assigned projects, create work items' },
  { value: 'read_only', label: 'Read Only', description: 'View projects but cannot modify anything' },
];

interface UserRoleEditorProps {
  user: PmoUserRecord;
  onSave: (userId: string, updates: { role?: PmoRole; assignedProjects?: string[]; additionalEmails?: string[]; gitIdentities?: string[] }) => Promise<void>;
  onClose: () => void;
  isSelf: boolean;
}

export default function UserRoleEditor({ user, onSave, onClose, isSelf }: UserRoleEditorProps) {
  const [role, setRole] = useState<PmoRole>(user.role);
  const [selectedProjects, setSelectedProjects] = useState<string[]>(user.assignedProjects || []);
  const [additionalEmails, setAdditionalEmails] = useState<string[]>(user.additionalEmails || []);
  const [newEmail, setNewEmail] = useState('');
  const [gitIdentities, setGitIdentities] = useState<string[]>(user.gitIdentities || []);
  const [newGit, setNewGit] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const projects = useProjectStore((s) => s.projects);
  const showProjectAssignment = role === 'project_resource';

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updates: { role?: PmoRole; assignedProjects?: string[]; additionalEmails?: string[]; gitIdentities?: string[] } = {};
      if (role !== user.role) updates.role = role;
      if (showProjectAssignment) updates.assignedProjects = selectedProjects;
      // Always send additionalEmails if changed
      const origEmails = user.additionalEmails || [];
      if (JSON.stringify(additionalEmails) !== JSON.stringify(origEmails)) {
        updates.additionalEmails = additionalEmails;
      }
      if (JSON.stringify(gitIdentities) !== JSON.stringify(user.gitIdentities || [])) {
        updates.gitIdentities = gitIdentities;
      }
      await onSave(user._id, updates);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const toggleProject = (projectId: string) => {
    setSelectedProjects((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit User</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{user.email}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* User Info */}
          <div className="flex items-center gap-3">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="w-12 h-12 rounded-full" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-accent/10 text-accent flex items-center justify-center text-lg font-bold">
                {user.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">{user.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
            </div>
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Role
            </label>
            <div className="space-y-2">
              {ROLE_OPTIONS.map((opt) => {
                const disabled = isSelf && opt.value !== 'super_admin';
                return (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                      role === opt.value
                        ? 'border-accent bg-accent/5 dark:bg-accent/10'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={opt.value}
                      checked={role === opt.value}
                      onChange={() => !disabled && setRole(opt.value)}
                      disabled={disabled}
                      className="mt-0.5 accent-accent"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{opt.label}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{opt.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Project Assignment (only for project_resource) */}
          {showProjectAssignment && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Assigned Projects
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Select which projects this user can access and modify work items in.
              </p>
              <div className="max-h-48 overflow-y-auto space-y-1.5 border border-gray-200 dark:border-gray-600 rounded-xl p-3">
                {projects.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-2">No projects available</p>
                ) : (
                  projects.map((project) => (
                    <label
                      key={project._id}
                      className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedProjects.includes(project._id)}
                        onChange={() => toggleProject(project._id)}
                        className="accent-accent rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{project.name}</span>
                      <span
                        className={`ml-auto text-xs px-1.5 py-0.5 rounded ${
                          project.status === 'active'
                            ? 'text-green-600 bg-green-50 dark:bg-green-900/20'
                            : 'text-gray-500 bg-gray-50 dark:bg-gray-700'
                        }`}
                      >
                        {project.status}
                      </span>
                    </label>
                  ))
                )}
              </div>
              <p className="mt-2 text-xs text-gray-400">
                {selectedProjects.length} project{selectedProjects.length !== 1 ? 's' : ''} selected
              </p>
            </div>
          )}

          {/* Additional Email Addresses */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Additional Email Addresses
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Add up to 2 additional email addresses for this user. They can sign in as this user.
            </p>
            {additionalEmails.map((email, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                />
                <button
                  type="button"
                  onClick={() => setAdditionalEmails((prev) => prev.filter((_, i) => i !== idx))}
                  className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-400 hover:text-red-600 transition-colors"
                  title="Remove email"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {additionalEmails.length < 2 && (
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const trimmed = newEmail.trim();
                      if (trimmed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) && !additionalEmails.includes(trimmed) && trimmed !== user.email) {
                        setAdditionalEmails((prev) => [...prev, trimmed]);
                        setNewEmail('');
                      }
                    }
                  }}
                  placeholder="Add email address"
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 placeholder:text-gray-400"
                />
                <button
                  type="button"
                  onClick={() => {
                    const trimmed = newEmail.trim();
                    if (trimmed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) && !additionalEmails.includes(trimmed) && trimmed !== user.email) {
                      setAdditionalEmails((prev) => [...prev, trimmed]);
                      setNewEmail('');
                    }
                  }}
                  className="p-2 rounded-lg hover:bg-accent/10 text-accent transition-colors"
                  title="Add email"
                >
                  <Plus size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Git identities: attribution only, never a sign-in (2026-10-19) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Git / GitHub Identities
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              The email addresses and logins this person's commits and GitHub activity use (e.g. a personal GitHub
              email or a noreply address). Work assigned to one lands on this user's primary email. These can never
              sign in.
            </p>
            {gitIdentities.map((g, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <input type="text" value={g} readOnly
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300" />
                <button type="button" title="Remove identity"
                  onClick={() => setGitIdentities((prev) => prev.filter((_, i) => i !== idx))}
                  className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-400 hover:text-red-600 transition-colors">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {gitIdentities.length < 10 && (
              <div className="flex items-center gap-2">
                <input type="text" value={newGit} onChange={(e) => setNewGit(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const t = newGit.trim();
                      if (t && !gitIdentities.includes(t) && t !== user.email) { setGitIdentities((prev) => [...prev, t]); setNewGit(''); }
                    }
                  }}
                  placeholder="Add GitHub email or login"
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 placeholder:text-gray-400" />
                <button type="button" title="Add identity"
                  onClick={() => {
                    const t = newGit.trim();
                    if (t && !gitIdentities.includes(t) && t !== user.email) { setGitIdentities((prev) => [...prev, t]); setNewGit(''); }
                  }}
                  className="p-2 rounded-lg hover:bg-accent/10 text-accent transition-colors">
                  <Plus size={16} />
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-accent hover:bg-accent/90 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
